/**
 * Share Actions - Share/unshare snippets with UI integration
 *
 * Handles POST/DELETE on v1/snippets/{snippetId}/share for setting isShared to true (shared)
 * or back to false (private). On success: updates the snippet in store (isShared) and
 * in the editor quick insert when editorAPI is passed. On error: shows error flag and
 * reports to monitoring.
 *
 * Call these after create/update/duplicate succeeds when the user's isShared differs
 * from the snippet's current value.
 */
import type { StoreActionApi } from 'react-sweet-state';
import type { IntlShape } from 'react-intl';
import { defineMessages } from 'react-intl';

import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { QuickInsertPlugin } from '@atlaskit/editor-plugin-quick-insert';

import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';

import * as BlockServiceClient from '../../services/blockServiceClient.ts';
import { reportError } from '../../utils/reportError.ts';
import { updateSnippetInQuickInsertItem } from '../../quick-insert/snippetQuickInsertItemApi.tsx';
import type { SnippetsState } from '../snippetsStore.types.ts';
import { showSnippetErrorFlag } from '../../utils/snippetFlags.ts';
import { transformSnippet } from '../../services/utils/blockServiceUtils.ts';
import { onCurrentFetchComplete } from '../../utils/pendingCallbacks.ts';
import type { Snippet } from '../../types/snippet.ts';

const shareActionsMessages = defineMessages({
	claimShareErrorFlagTitle: {
		id: 'snippets.shareActions.claimShareErrorFlagTitle',
		defaultMessage: 'Could not access template',
		description: 'Error flag title when claiming a snippet via share link fails',
	},
	claimShareErrorFlagDescription: {
		id: 'snippets.shareActions.claimShareErrorFlagDescription',
		defaultMessage: 'The share link may be invalid.',
		description: 'Error flag description when claiming a snippet via share link fails',
	},
	shareErrorFlagTitle: {
		id: 'snippets.shareActions.shareErrorFlagTitle',
		defaultMessage: 'Could not set template to shared',
		description: 'Error flag title when share API fails after create/edit/duplicate',
	},
	shareErrorFlagDescription: {
		id: 'snippets.shareActions.shareErrorFlagDescription',
		defaultMessage: 'The template was saved but access update failed. Please try again.',
		description: 'Error flag description when setting access level to shared API fails',
	},
	unshareErrorFlagTitle: {
		id: 'snippets.shareActions.unshareErrorFlagTitle',
		defaultMessage: 'Could not set template to private',
		description: 'Error flag title when unshare API fails after edit',
	},
	unshareErrorFlagDescription: {
		id: 'snippets.shareActions.unshareErrorFlagDescription',
		defaultMessage: 'The template was saved but access update failed. Please try again.',
		description: 'Error flag description when setting access level to private API fails',
	},
});

/**
 * Options for claimSnippetShare that enable error flag display and success handling.
 */
export type ClaimSnippetShareOptions = {
	flags: WithFlagsProps['flags'];
	intl: IntlShape;
	onClaimSuccess?: (snippetId: string) => void;
};

/**
 * Options for shareSnippet/unshareSnippet that enable UI feedback and editor integration.
 */
export type ShareSnippetOptions = {
	flags: WithFlagsProps['flags'];
	intl: IntlShape;
	editorAPI?: PublicPluginAPI<[QuickInsertPlugin]> | null;
	onShareFailure?: (snippetId: string, error: Error) => void;
	onUnshareFailure?: (snippetId: string, error: Error) => void;
};

type ShareActionContext = StoreActionApi<SnippetsState>;

function updateSnippetStoreAndQuickInsert(params: {
	getState: ShareActionContext['getState'];
	setState: ShareActionContext['setState'];
	snippetId: string;
	isShared: boolean;
	options: ShareSnippetOptions | undefined;
}): void {
	const { getState, setState, snippetId, isShared, options } = params;
	const { snippets } = getState();
	const updatedSnippets = snippets.map((snippet) =>
		snippet.snippetId === snippetId ? { ...snippet, isShared } : snippet,
	);

	setState({ snippets: updatedSnippets });

	if (options?.editorAPI) {
		const updatedSnippet = updatedSnippets.find((s) => s.snippetId === snippetId);
		if (updatedSnippet) {
			updateSnippetInQuickInsertItem({
				snippet: updatedSnippet,
				editorAPI: options.editorAPI,
			});
		}
	}
}

/**
 * Actions for sharing and unsharing snippets with UI integration.
 */
export const shareActions = {
	/**
	 * Claims a snippet via a share link. POSTs to the claim endpoint with the shareId.
	 * On success: adds the snippet to the store (if not already present) and opens the detail panel.
	 * On error: reports to monitoring and shows an error flag when options provided. Does not throw.
	 *
	 * @param snippetId - The snippet ID from the share URL
	 * @param shareId - The share token from the share URL
	 * @param options - Optional flags and intl for error flag display, plus success handling
	 */
	claimSnippetShare:
		(snippetId: string, shareId: string, options?: ClaimSnippetShareOptions) =>
		async ({ getState, setState }: ShareActionContext): Promise<void> => {
			setState({ isClaimingShare: true });
			try {
				const snippetData = await BlockServiceClient.claimSnippetShare(snippetId, shareId);
				options?.onClaimSuccess?.(snippetData.snippetId);
				const snippet = await transformSnippet(snippetData);
				if (snippet) {
					const applyToStore = (currentSnippets: Snippet[]) => {
						const { templateGallery } = getState();
						const alreadyExists = currentSnippets.some((s) => s.snippetId === snippet.snippetId);
						setState({
							isClaimingShare: false,
							snippets: alreadyExists ? currentSnippets : [...currentSnippets, snippet],
							templateGallery: { ...templateGallery, selectedSnippetId: snippet.snippetId },
						});
					};

					if (getState().isLoading) {
						// fetchSnippets is still in flight — defer until it settles so the fetch
						// result doesn't overwrite the claimed snippet
						onCurrentFetchComplete(applyToStore);
					} else {
						applyToStore(getState().snippets);
					}
				} else {
					setState({ isClaimingShare: false });
				}
			} catch (error) {
				const err = error instanceof Error ? error : new Error(String(error));
				reportError(err);
				setState({ isClaimingShare: false });
				if (options) {
					showSnippetErrorFlag(
						options.flags,
						options.intl.formatMessage(shareActionsMessages.claimShareErrorFlagTitle),
						options.intl.formatMessage(shareActionsMessages.claimShareErrorFlagDescription),
					);
				}
			}
		},

	/**
	 * Shares a snippet with the site.
	 * On success: updates the snippet in store (isShared: true) and in quick insert when editorAPI provided.
	 * On error: reports to monitoring and shows error flag when options provided. Does not throw.
	 *
	 * @param snippetId - The snippet ID to share
	 * @param options - Optional flags, intl, and editorAPI for UI feedback and quick insert update
	 */
	shareSnippet:
		(snippetId: string, options?: ShareSnippetOptions) =>
		async ({ getState, setState }: ShareActionContext): Promise<void> => {
			try {
				await BlockServiceClient.shareSnippet(snippetId);
				updateSnippetStoreAndQuickInsert({
					getState,
					setState,
					snippetId,
					isShared: true,
					options,
				});
			} catch (error) {
				const err = error instanceof Error ? error : new Error(String(error));
				reportError(err);
				if (options) {
					showSnippetErrorFlag(
						options.flags,
						options.intl.formatMessage(shareActionsMessages.shareErrorFlagTitle),
						options.intl.formatMessage(shareActionsMessages.shareErrorFlagDescription),
					);
					options.onShareFailure?.(snippetId, err);
				}
			}
		},

	/**
	 * Unshares a snippet.
	 * On success: updates the snippet in store (isShared: false) and in quick insert when editorAPI provided.
	 * On error: reports to monitoring and shows error flag when options provided. Does not throw.
	 *
	 * @param snippetId - The snippet ID to unshare
	 * @param options - Optional flags, intl, and editorAPI for UI feedback and quick insert update
	 */
	unshareSnippet:
		(snippetId: string, options?: ShareSnippetOptions) =>
		async ({ getState, setState }: ShareActionContext): Promise<void> => {
			try {
				await BlockServiceClient.unshareSnippet(snippetId);
				updateSnippetStoreAndQuickInsert({
					getState,
					setState,
					snippetId,
					isShared: false,
					options,
				});
			} catch (error) {
				const err = error instanceof Error ? error : new Error(String(error));
				reportError(err);
				if (options) {
					showSnippetErrorFlag(
						options.flags,
						options.intl.formatMessage(shareActionsMessages.unshareErrorFlagTitle),
						options.intl.formatMessage(shareActionsMessages.unshareErrorFlagDescription),
					);
					options.onUnshareFailure?.(snippetId, err);
				}
			}
		},
};
